import { useMemo } from "react";
import DOMPurify from "dompurify";

type DangerousInnerHTMLProps = {
  Node?: "div" | "span";
  html?: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
};

/**
 * Renders an HTML string, sanitized with DOMPurify before it reaches the DOM.
 *
 * Sanitizing here rather than at each call site means every consumer is covered by
 * default, including any added later. DOMPurify's default profile keeps the markup our
 * callers actually produce -- formatting tags, links, tables, images and inline styles --
 * while stripping <script>, on* event handler attributes and javascript: URLs.
 *
 * `target` is re-allowed because it is not in DOMPurify's default attribute list, and our
 * markdown content (terms of use, help documents, PIMS onboarding instructions) relies on
 * target="_blank" to open external links in a new tab.
 *
 * More info at: https://react.dev/reference/react-dom/components/common#dangerously-setting-the-inner-html
 */
const SANITIZE_CONFIG = { ADD_ATTR: ["target"] };

const DangerousInnerHTML = ({
  Node = "div",
  html = "",
  className = "",
  onClick,
}: DangerousInnerHTMLProps) => {
  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(html, SANITIZE_CONFIG), [html]);

  return (
    /* eslint-disable-next-line react/no-danger */
    <Node
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      onClick={onClick}
    />
  );
};

export default DangerousInnerHTML;
